import {action, observable, makeObservable} from 'mobx';
import {priority, wcfclient} from '@priority-software/priority-proto-api/lib';
import {getForm} from '../service/api/general';
import {mapRecordValue} from '../../../utils/proto-api-utils/utils';

import {getAdditionalProps} from '../../../services/proto/core/loginProps';

import {isRTL} from '../../../Localization';
import {OB_Screen} from './OB_Screen';
import {
    getFinalStepData,
    isStepFinished,
    isSubmittable,
    updateQuestionValue
} from '../screens/Questionnaire/utils';
import {onboardingMessageStore} from './OB_Message_Store';
import {
    getOnboardingState,
    isQuestionnaireCompleted,
    setQuestionnaireCompleted
} from '../service/completionState';
import {finishQuiz, getQuestionDescription, updateQuestion} from '../service/api/questionnaire';
import {sendMonitor} from '../../../utils/monitoring';
import {cleanContent} from '../../../utils/cleanHTMLContent';

type IField = priority.netitems.form.IField;

export type Category = {
    catID: IField['Value'];
    catName: IField['Value'];
};

export type Step = {
    id: string;
    subject: string;
    title: string;
    questions: Array<any>;
    semiFilled: boolean;
    isFinished: boolean;
    type: 'question' | 'summary';
};

const formName = 'ONBOARDINGLINESONE';

class OB_Questionnaire_Store extends OB_Screen {
    resetStore = () => {
        // reset store to default values
        this.procedureLoading = false;
        this.stepLoader = false;
        this.submittable = false;
        this.activeStepID = '0';
        this.questionCategories = [];
        this.questionRecords = [];
        this.steps = {};
        this.completed = false;
    };

    questionRecords = [];

    @observable procedureLoading: boolean = false;
    @observable stepLoader: boolean = false;
    @observable submittable: boolean = false;
    @observable activeStepID: string = getOnboardingState().OB_questionnaire_step || '0';
    @observable questionCategories: Array<Category> = [];
    @observable steps: {[key: string]: Step} = {};
    @observable completed = false;

    constructor() {
        super();

        makeObservable(this);
    }

    @action toggleProcedureLoader = (flag: boolean) => {
        this.procedureLoading = flag;
    };

    @action setCompleted = async () => {
        await setQuestionnaireCompleted();
        this.completed = true;
    };

    @action finalizeQuiz = async () => {
        try {
            this.toggleProcedureLoader(true);

            await this.saveAllAnswers();
            await finishQuiz();
            await this.setCompleted();

            await this.goToNextStep(this.activeStepID);

            this.toggleProcedureLoader(false);
        } catch (e) {
            this.handleError(e);
        }
    };

    getQuestionDescription = async (questionIndex: number) => {
        try {
            const {form, cols} = await getForm(formName);

            const Keys = cols.Columns.filter(it => it.Key).map(col => {
                const val = this.questionRecords[questionIndex][col.Name];
                return {ColumnID: col.ID, Value: mapRecordValue(val, col.Type)};
            });

            const desc = await getQuestionDescription(form, cols, Keys);

            return cleanContent(desc, true);
        } catch (e) {
            throw Error((e as Error).message);
        }
    };

    @action qetQuestionnaireData = async () => {
        this.setLoading(true);

        this.completed = await isQuestionnaireCompleted();

        if (!this.completed) {
            try {
                if (Object.keys(this.steps).length === 0) {
                    const questions = await this.getQuestions();

                    // Set Question categories
                    this.questionCategories = [
                        ...(new Map(
                            questions.map((item: any) => {
                                return [
                                    item.SUBCATENAME,
                                    {
                                        catID: item.SUBCATENAME,
                                        catName: isRTL() ? item.SUBCATNAME : item.SUBCATENAME
                                    }
                                ];
                            })
                        ).values() as any)
                    ];

                    const mainQuestions = questions
                        .map((item, index) => ({...item, ScrLine: index}))
                        .filter((item: any) => !!item.MAIN);

                    const descriptions = {};

                    for (const q of mainQuestions) {
                        const questionDesc = await this.getQuestionDescription(q.ScrLine);
                        descriptions[q.ScrLine] = questionDesc;
                    }

                    const mappedQuestions = questions.map((item: any, index: number) => {
                        return {
                            questionID: item.ONBOARDINGLINE,
                            question: isRTL() ? item.QUESTION : item.QUESTIONE || item.QUESTION,
                            value: item.ANSNUM,
                            subCategory: item.SUBCATENAME,
                            ScrLine: index,
                            isMain: !!item.MAIN,
                            text: descriptions[index]
                        };
                    });

                    const mappedSteps = this.questionCategories.reduce((prev, acc, index) => {
                        const stepQuestion = mappedQuestions.filter(
                            item => item.subCategory === acc.catID
                        );

                        return {
                            ...prev,
                            [index]: {
                                id: `${index}`,
                                subject: acc.catID,
                                title: acc.catName,
                                questions: stepQuestion,
                                semiFilled: false,
                                isFinished: isStepFinished(stepQuestion),
                                type: 'question'
                            }
                        };
                    }, {});

                    if (Object.keys(mappedSteps).length > 0) {
                        // Create a Final step data;
                        mappedSteps[this.questionCategories.length] = {
                            id: `${this.questionCategories.length}`,
                            ...getFinalStepData()
                        };

                        this.steps = mappedSteps;
                        this.submittable = isSubmittable(mappedSteps);
                    } else {
                        const errorMsg =
                            "Quiz prepare error, it looks like questions wasn't added yet";
                        const messageData = {message: {MType: 3}};

                        onboardingMessageStore.setMessage(messageData);
                        throw Error(errorMsg);
                    }
                }
            } catch (e) {
                this.handleError(e);
            }
        }
        this.setLoading(false);
    };

    @action setActiveStep = (activeStepID: string) => {
        if (activeStepID !== this.activeStepID) {
            this.activeStepID = activeStepID;
        }
    };

    @action toggleStepSubQuestions = (flag: boolean) => {
        this.steps = {
            ...this.steps,
            [this.activeStepID]: {
                ...this.steps[this.activeStepID],
                semiFilled: flag
            }
        };
    };

    @action goToStep = async (nextStepId: string) => {
        this.stepLoader = true;

        await this.saveAllAnswers();
        const currStep = this.steps[this.activeStepID];

        const updatedStepData =
            currStep.type === 'question'
                ? {
                      ...currStep,
                      semiFilled: false,
                      isFinished: isStepFinished(currStep.questions)
                  }
                : currStep;

        this.steps = {...this.steps, [this.activeStepID]: updatedStepData};
        this.stepLoader = false;
        this.submittable = isSubmittable(this.steps);
        this.setActiveStep(nextStepId);
    };

    goToNextStep = async (id: string) => {
        const nextStepId = `${Number(id) + 1}`;
        // update current step status and set next stepIa
        return await this.goToStep(nextStepId);
    };

    goToPrevStep = async (currentId: string) => {
        const prevStepIndex = Number(currentId) - 1;
        const prevStepId = prevStepIndex >= 0 ? `${prevStepIndex}` : currentId;

        await this.goToStep(prevStepId);
    };

    saveAnswer = async (val: number, ScrLine: number) => {
        const {
            form: {Session},
            cols
        } = await getForm(formName);

        const Column = cols.Columns.find(col => col.Name === 'ANSNUM');
        const ColumnID = Column?.ID;
        const currentRecord = this.questionRecords[ScrLine];

        const Value = mapRecordValue(val, Column?.Type);

        const Keys = cols.Columns.filter(col => col.Key).map(col => ({
            ColumnID: col.ID,
            Value: mapRecordValue(currentRecord[col.Name], col?.Type)
        }));

        try {
            return await updateQuestion({Session, Value, Keys, ColumnID, ScrLine});
        } catch (e) {
            throw new Error((e as Error).message);
        }
    };

    saveAllAnswers = async () => {
        try {
            const {steps, activeStepID} = this;
            const currentStepQuestions = steps[activeStepID]?.questions || [];

            const filteredQ = currentStepQuestions.filter(q => {
                const previousVal = this.questionRecords.find(
                    item => item.ONBOARDINGLINE === q.questionID
                );

                return previousVal?.ANSNUM !== q.value;
            });

            const save = async () => {
                for (const q of filteredQ) {
                    await this.saveAnswer(q.value, q.ScrLine);

                    this.questionRecords = this.questionRecords.map(item => {
                        return q.questionID === item.ONBOARDINGLINE
                            ? {...item, ANSNUM: q.value}
                            : item;
                    });
                }
            };

            return await save();
        } catch (e) {
            this.handleError(e);
        }
    };

    handleQuestionnaireMonitoring = (val: number, index: number) => {
        const record = this.questionRecords[index];
        if (record.MAIN === 'Y') {
            const title = record.SUBCATENAME;
            const answer = val === 1 ? 'Yes' : val === 2 ? 'No' : null;
            const monitorText = `OnboardingTabsQustions${title}${answer}`;
            sendMonitor(monitorText);
        }
    };

    updateAnswerValue = async (value: number, ScrLine: number) => {
        const {activeStepID, steps} = this;
        const activeStepData = steps[activeStepID];

        const updatedQuestions = updateQuestionValue(activeStepData.questions, ScrLine, value);

        this.steps = {
            ...this.steps,
            [activeStepID]: {
                ...activeStepData,
                questions: updatedQuestions
            }
        };

        this.submittable = isSubmittable(this.steps);
    };

    getQuestions = async () => {
        try {
            const {cols, form} = await getForm(formName);

            const columnsMap = cols.Columns.reduce((map, curr) => {
                return typeof curr.ID === 'number' ? {...map, [curr.ID]: curr.Name} : map;
            }, {});

            const records = await wcfclient.recordsFetch(
                new priority.netitems.form.FetchRq({
                    Session: form.Session,
                    Properties: getAdditionalProps(),
                    Filters: [
                        {
                            Condition: [
                                {
                                    ColumnID: cols.Columns.find(c => c.Name === 'CATENAME')?.ID,
                                    Operator: priority.netitems.shared.ComparisonOperator.Eq,
                                    Values: ['Onboarding Zoom IL']
                                }
                            ]
                        }
                    ]
                })
            );

            const questions = records.Records.map(record => {
                return record.Fields.reduce((prevData, currField) => {
                    const fieldValue = Object.values(currField.Value)[0];
                    const fieldId = Number(currField.ColumnID);
                    const fieldName = columnsMap[fieldId];

                    return fieldName ? {...prevData, [fieldName]: fieldValue} : prevData;
                }, {});
            });

            this.questionRecords = questions;

            return questions;
        } catch (e) {
            throw new Error((e as Error).message);
        }
    };

    handleError = e => {
        this.procedureLoading = false;
        this.setLoading(false);
        console.error(e);
    };
}

export const questionnaireStore = new OB_Questionnaire_Store();
